import { useState, useEffect, useRef } from 'react';
import { X, Send, Smile, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Message } from './Message';
import { cn } from '../../lib/utils';
import { BACKEND_URL } from '@/config/api';

export const ThreadPanel = ({
  parentMessage,
  channelId,
  channelName,
  token,
  currentUserId,
  onClose,
  onReact,
  onMessageSent
}) => {
  const [replies, setReplies] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const fetchThread = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channelId}/threads/${parentMessage.id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setReplies(data.replies);
        setParticipants(data.participants);
      }
    } catch (error) {
      console.error('Error fetching thread:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchThread();
  }, [parentMessage.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies]);

  const handleSendReply = async () => {
    if (!newReply.trim() || isSending) return;

    setIsSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/channels/${channelId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content: newReply,
          thread_id: parentMessage.id
        })
      });

      if (response.ok) {
        const sentMessage = await response.json();
        setReplies(prev => [...prev, sentMessage]);
        setNewReply('');
        onMessageSent?.(sentMessage);
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const handleEdit = async (messageId, content) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channelId}/messages/${messageId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ content })
        }
      );
      if (response.ok) {
        const updated = await response.json();
        setReplies(prev => prev.map(r => r.id === messageId ? updated : r));
      }
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const handleDelete = async (messageId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/channels/${channelId}/messages/${messageId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      if (response.ok) {
        setReplies(prev => prev.filter(r => r.id !== messageId));
      }
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  return (
    <div className="w-96 bg-[#1a1d21] border-l border-gray-700 flex flex-col h-full" data-testid="thread-panel">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="font-bold text-white">Thread</h3>
          <p className="text-xs text-gray-400">#{channelName}</p>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onClose}
          className="text-gray-400 hover:text-white"
        >
          <X size={20} />
        </Button>
      </div>

      {/* Thread Content */}
      <div className="flex-1 overflow-y-auto">
        {/* Parent Message */}
        <div className="border-b border-gray-700 pb-4">
          <Message
            message={parentMessage}
            currentUserId={currentUserId}
            onReact={onReact}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onOpenThread={() => {}}
          />
        </div>

        {/* Reply Count */}
        <div className="px-5 py-3 flex items-center gap-2">
          <div className="flex-1 border-t border-gray-700" />
          <span className="text-xs text-gray-400">
            {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
          </span>
          <div className="flex-1 border-t border-gray-700" />
        </div>

        {/* Replies */}
        {isLoading ? (
          <div className="px-5 py-4 text-center text-gray-500">Loading replies...</div>
        ) : (
          <div className="pb-4">
            {replies.map(reply => (
              <Message
                key={reply.id}
                message={reply}
                currentUserId={currentUserId}
                onReact={onReact}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onOpenThread={() => {}}
                isThreadReply
              />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Reply Input */}
      <div className="p-4 border-t border-gray-700">
        <div className="bg-[#222529] border border-gray-700 rounded-lg focus-within:border-gray-500">
          <textarea
            ref={inputRef}
            value={newReply}
            onChange={(e) => setNewReply(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply..."
            className="w-full px-3 py-2 bg-transparent text-white text-sm resize-none focus:outline-none"
            rows={2}
            data-testid="thread-reply-input"
          />
          <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-700">
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-white">
                <Paperclip size={16} />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-white">
                <Smile size={16} />
              </Button>
            </div>
            <Button
              onClick={handleSendReply}
              disabled={!newReply.trim() || isSending}
              size="sm"
              className="bg-[#007a5a] hover:bg-[#006649] text-white disabled:opacity-50"
              data-testid="thread-send-btn"
            >
              <Send size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
